import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import Waveform from "./Waveform";
import CountUp from "@/components/wishire/CountUp";
import {
  FileText,
  Mic,
  GraduationCap,
  Brain,
  BookOpen,
  TrendingUp,
} from "lucide-react";

/* ─── helpers ─────────────────────────────────────── */
const CARD_VARIANTS = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const HOVER = {
  y: -4,
  boxShadow: "0 24px 60px -30px rgba(28,25,23,0.2)",
  transition: { duration: 0.22, ease: "easeOut" },
};

/* ─── sub-components ──────────────────────────────── */
function AnimatedScorePill({ label, value, inView }) {
  return (
    <div className="rounded-xl bg-white border border-stone-200 py-2 px-1 text-center">
      <p className="font-display text-xl">
        <CountUp value={inView ? value : 0} duration={900} />
      </p>
      <p className="overline mt-0.5">{label}</p>
    </div>
  );
}

function ATSBar({ inView }) {
  return (
    <div className="mt-5 flex items-center gap-3 text-sm">
      <span className="font-mono text-xs text-stone-500 shrink-0">ATS Score</span>
      <div className="flex-1 h-2 rounded-full bg-stone-200 overflow-hidden">
        <motion.div
          className="h-full bg-[#15803D] rounded-full"
          initial={{ width: 0 }}
          animate={{ width: inView ? "91%" : "0%" }}
          transition={{ duration: 1.1, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
        />
      </div>
      <span className="font-display text-lg">
        <CountUp value={inView ? 91 : 0} duration={1100} />
      </span>
    </div>
  );
}

function ChatDemo() {
  const [phase, setPhase] = useState(0);
  // phase 0: typing indicator, phase 1: message shown
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 1400);
    return () => clearTimeout(t1);
  }, []);

  return (
    <div className="mt-5 rounded-2xl bg-stone-800 p-4 space-y-3">
      {/* AI bubble */}
      <div className="flex items-start gap-2">
        <span className="shrink-0 w-6 h-6 rounded-full bg-amber-300 flex items-center justify-center font-mono text-[9px] text-stone-900 font-bold">
          AI
        </span>
        <div className="rounded-2xl rounded-tl-none bg-stone-700 px-3 py-2 text-xs text-stone-200 leading-relaxed max-w-[85%]">
          {phase === 0 ? (
            <span className="flex gap-1 items-center h-4">
              <motion.span
                className="w-1.5 h-1.5 rounded-full bg-stone-400"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ repeat: Infinity, duration: 1, delay: 0 }}
              />
              <motion.span
                className="w-1.5 h-1.5 rounded-full bg-stone-400"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
              />
              <motion.span
                className="w-1.5 h-1.5 rounded-full bg-stone-400"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ repeat: Infinity, duration: 1, delay: 0.4 }}
              />
            </span>
          ) : (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              Should I take this offer? It pays 18% more but the team is only 3 people.
            </motion.span>
          )}
        </div>
      </div>
      {/* User bubble */}
      {phase === 1 && (
        <motion.div
          className="flex items-start gap-2 justify-end"
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.35, delay: 0.5 }}
        >
          <div className="rounded-2xl rounded-tr-none bg-[#C2410C]/90 px-3 py-2 text-xs text-stone-100 leading-relaxed max-w-[85%]">
            Great signal — early-stage leverage beats a safe title right now. Here&rsquo;s what to negotiate first…
          </div>
          <span className="shrink-0 w-6 h-6 rounded-full bg-stone-600 flex items-center justify-center font-mono text-[9px] text-stone-200 font-bold">
            You
          </span>
        </motion.div>
      )}
    </div>
  );
}

/* ─── card wrapper ─────────────────────────────────── */
function Card({ children, className = "", tone = "light", custom = 0 }) {
  const styles =
    tone === "dark"
      ? "bg-stone-900 text-stone-50 border-stone-900"
      : tone === "accent"
        ? "bg-[#FEF3C7] border-[#FDE68A]"
        : "bg-white border-stone-200";

  return (
    <motion.div
      custom={custom}
      variants={CARD_VARIANTS}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      whileHover={HOVER}
      className={`relative rounded-3xl border p-7 md:p-8 ${styles} ${className}`}
    >
      {children}
    </motion.div>
  );
}

function Icon({ children, dark }) {
  return (
    <div
      className={`inline-flex p-3 rounded-2xl ${
        dark ? "bg-stone-800 text-amber-300" : "bg-stone-100 text-[#C2410C]"
      }`}
    >
      {children}
    </div>
  );
}

/* ─── Voice card — needs its own inView ref ─────────── */
function VoiceCard({ custom }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <Card className="md:col-span-7 md:row-span-2" custom={custom}>
      <div ref={ref} className="flex items-start justify-between">
        <div>
          <span className="overline">01 · Voice mock interviews</span>
          <h3 className="font-display text-3xl mt-2 leading-tight">
            Talk to an AI that listens like a hiring manager.
          </h3>
        </div>
        <div className="p-3 rounded-2xl bg-stone-100 shrink-0">
          <Mic size={20} className="text-[#C2410C]" />
        </div>
      </div>
      <p className="mt-4 text-stone-600 leading-relaxed">
        Role-specific question banks. Real-time transcription, scoring on
        clarity, depth, structure and STAR-method answers.
      </p>
      <div className="mt-6 p-5 rounded-2xl bg-[#F5F3ED] border border-stone-200">
        <div className="flex items-center justify-between mb-3">
          <span className="overline">Live transcript</span>
          <span className="font-mono text-xs text-emerald-700 flex items-center gap-1">
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-emerald-600"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ repeat: Infinity, duration: 1.2 }}
            />
            recording
          </span>
        </div>
        <p className="text-stone-800 leading-relaxed">
          &ldquo;Walk me through a time you de-scoped a feature under deadline
          pressure. What did you measure, who did you align with, and
          what did you trade off?&rdquo;
        </p>
        <div className="mt-4">
          <Waveform active={inView} />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          <AnimatedScorePill label="Clarity" value={92} inView={inView} />
          <AnimatedScorePill label="Structure" value={84} inView={inView} />
          <AnimatedScorePill label="Depth" value={88} inView={inView} />
        </div>
      </div>
    </Card>
  );
}

/* ─── Resume card — needs inView for ATS bar ────────── */
function ResumeCard({ custom }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <Card className="md:col-span-5" custom={custom}>
      <div ref={ref}>
        <Icon><FileText size={20} /></Icon>
        <span className="overline mt-4 block">02 · AI Resume Builder</span>
        <h3 className="font-display text-2xl mt-1">
          Resume that survives the ATS — and impresses humans after.
        </h3>
        <p className="mt-3 text-stone-600 leading-relaxed">
          Tailored bullet points, keyword fit per JD, instant ATS score
          with line-by-line fixes.
        </p>
        <ATSBar inView={inView} />
      </div>
    </Card>
  );
}

/* ─── Career Coach card ──────────────────────────────── */
function CoachCard({ custom }) {
  return (
    <Card className="md:col-span-5" tone="dark" custom={custom}>
      <Icon dark><Brain size={20} /></Icon>
      <span className="overline mt-4 block" style={{ color: "rgba(253,251,247,0.55)" }}>
        03 · AI Career Coach
      </span>
      <h3 className="font-display text-2xl mt-1 text-stone-50">
        Your career, with a 24/7 second opinion.
      </h3>
      <p className="mt-3 text-stone-300 leading-relaxed">
        Ask anything — &ldquo;Should I take this offer?&rdquo; or &ldquo;What&apos;s missing on
        my path to staff engineer?&rdquo; — and get a real answer with sources.
      </p>
      <ChatDemo />
    </Card>
  );
}

/* ─── main export ─────────────────────────────────────── */
export default function CandidateFeatures() {
  return (
    <section id="for-students" className="section">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-7">
            <span className="overline">For candidates</span>
            <h2 className="font-display mt-3 text-4xl md:text-5xl leading-tight">
              Every interview,{" "}
              <span className="italic text-stone-500">rehearsed.</span>
              <br />
              Every resume,{" "}
              <span className="italic text-stone-500">sharpened.</span>
            </h2>
          </div>
          <p className="md:col-span-5 text-stone-600 text-lg leading-relaxed">
            Five AI tools that compound. Build a portfolio of proof — not just
            another PDF in a recruiter&apos;s inbox.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <VoiceCard custom={0} />
          <ResumeCard custom={1} />
          <CoachCard custom={2} />

          {/* Skill gap */}
          <Card className="md:col-span-4" custom={3}>
            <Icon><TrendingUp size={20} /></Icon>
            <span className="overline mt-4 block">04 · Skill gap analysis</span>
            <h3 className="font-display text-2xl mt-1">
              See exactly what stands between you and that role.
            </h3>
          </Card>

          {/* Courses */}
          <Card className="md:col-span-4" custom={4}>
            <Icon><BookOpen size={20} /></Icon>
            <span className="overline mt-4 block">05 · Course matchmaking</span>
            <h3 className="font-display text-2xl mt-1">
              Curated, not crowded. The 3 courses that close your gap.
            </h3>
          </Card>

          {/* Wishire portfolio */}
          <Card className="md:col-span-4" tone="accent" custom={5}>
            <Icon><GraduationCap size={20} /></Icon>
            <span className="overline mt-4 block">06 · Wishire Portfolio</span>
            <h3 className="font-display text-2xl mt-1">
              One shareable link. Proof of work, not a list of jobs.
            </h3>
          </Card>
        </div>
      </div>
    </section>
  );
}
